import React, { useEffect, useState } from "react";
import {
  Box,
  Collapse,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import { formatMapPinScheduledDate } from "app/utils/mapPinTooltip";

export type SelectedDeliveryItem = {
  id: string | number;
  pickingNumber?: string;
  name?: string;
  partnerName?: string;
  scheduledDate?: string;
};

type SelectedDeliveriesListProps = {
  items: SelectedDeliveryItem[];
  userTimezone: string;
  defaultOpen?: boolean;
  onRemove?: (id: string | number) => void;
  onHighlight?: (id: string | number | null) => void;
};

export function SelectedDeliveriesList({
  items,
  userTimezone,
  defaultOpen = false,
  onRemove,
  onHighlight,
}: SelectedDeliveriesListProps) {
  const [open, setOpen] = useState(defaultOpen);
  const selectedCount = items.length;

  useEffect(() => {
    if (selectedCount === 0) {
      setOpen(false);
      onHighlight?.(null);
    }
  }, [onHighlight, selectedCount]);

  useEffect(() => {
    if (!open) {
      onHighlight?.(null);
    }
  }, [onHighlight, open]);

  useEffect(() => {
    if (defaultOpen && selectedCount > 0) {
      setOpen(true);
    }
  }, [defaultOpen, selectedCount]);

  return (
    <Box
      sx={{
        borderRadius: "10px",
        bgcolor: selectedCount > 0 ? "#f0fdf4" : "#f8fafc",
        border: `1px solid ${selectedCount > 0 ? "#bbf7d0" : "#eef1f4"}`,
        overflow: "hidden",
      }}
    >
      <Box
        role={selectedCount > 0 ? "button" : undefined}
        tabIndex={selectedCount > 0 ? 0 : undefined}
        onClick={() => {
          if (selectedCount > 0) {
            setOpen((current) => !current);
          }
        }}
        onKeyDown={(event) => {
          if (selectedCount > 0 && (event.key === "Enter" || event.key === " ")) {
            event.preventDefault();
            setOpen((current) => !current);
          }
        }}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          px: 1,
          py: 0.65,
          cursor: selectedCount > 0 ? "pointer" : "default",
          bgcolor: selectedCount > 0 ? "#f0fdf4" : "#f8fafc",
        }}
      >
        <LocalShippingOutlinedIcon
          sx={{ fontSize: 16, color: selectedCount > 0 ? "#5fa67a" : "#94a3b8" }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: "0.68rem", color: "#94a3b8", lineHeight: 1.2 }}>
            Selected deliveries
          </Typography>
          <Typography
            sx={{
              fontSize: "0.82rem",
              fontWeight: 700,
              color: selectedCount > 0 ? "#166534" : "#334155",
            }}
          >
            {selectedCount}
          </Typography>
        </Box>
        {selectedCount > 0 && (
          <ExpandMoreIcon
            sx={{
              fontSize: 20,
              color: "#64748b",
              transform: open ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.2s ease",
            }}
          />
        )}
      </Box>

      <Collapse in={open && selectedCount > 0}>
        <Box
          sx={{
            borderTop: "1px solid #eef1f4",
            maxHeight: 180,
            overflowY: "auto",
            bgcolor: "#f8fafc",
          }}
        >
          {items.map((item) => (
            <Box
              key={item.id}
              onMouseEnter={() => onHighlight?.(item.id)}
              onMouseLeave={() => onHighlight?.(null)}
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 0.5,
                px: 1,
                py: 0.75,
                bgcolor: "#f8fafc",
                borderBottom: "1px solid #eef1f4",
                transition: "background-color 0.15s ease",
                "&:hover": {
                  bgcolor: "#eef2f6",
                },
                "&:last-of-type": { borderBottom: "none" },
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: "0.76rem",
                    fontWeight: 700,
                    color: "#334155",
                    lineHeight: 1.3,
                  }}
                >
                  {item.pickingNumber || item.name || "—"}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.68rem",
                    color: "#475569",
                    lineHeight: 1.35,
                    mt: 0.15,
                  }}
                  noWrap
                  title={item.partnerName}
                >
                  {item.partnerName || "—"}
                </Typography>
                {item.scheduledDate && (
                  <Typography sx={{ fontSize: "0.64rem", color: "#94a3b8", mt: 0.15 }}>
                    {formatMapPinScheduledDate(item.scheduledDate, userTimezone)}
                  </Typography>
                )}
              </Box>
              {onRemove && (
                <Tooltip title="Remove from selection">
                  <IconButton
                    size="small"
                    aria-label={`Remove ${item.pickingNumber || item.name}`}
                    onClick={(event) => {
                      event.stopPropagation();
                      onRemove(item.id);
                    }}
                    sx={{
                      width: 24,
                      height: 24,
                      color: "#64748b",
                      "&:hover": { color: "#ef4444", bgcolor: "#fef2f2" },
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
}
